import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatCHF, formatDateTime, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n/I18nContext";
import { Gavel, Trophy, Dices } from "lucide-react";
import { toast } from "sonner";

export default function AuctionPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const t = useT();
  const [auction, setAuction] = useState(null);
  const [amount, setAmount] = useState(100);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/auctions/${id}`);
      setAuction(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => {
    load();
    const tm = setInterval(load, 5000);
    return () => clearInterval(tm);
  }, [id]);

  if (!auction) return <div className="p-10 text-center text-[#94A3B8]">{t("common.loading")}</div>;

  const placeBid = async (e) => {
    e.preventDefault();
    if (amount < 0 || amount % 100 !== 0) {
      toast.error(t("auction.toast.must_be_100"));
      return;
    }
    setLoading(true);
    try {
      await api.post(`/auctions/${id}/bid`, { amount: parseInt(amount) });
      toast.success(t("auction.toast.bid_placed", { amount: formatCHF(amount) }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const closeAuction = async () => {
    if (!window.confirm(t("auction.confirm_close"))) return;
    try {
      await api.post(`/auctions/${id}/close`);
      toast.success(t("auction.toast.closed"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const runLottery = async () => {
    if (!window.confirm(t("auction.confirm_lottery"))) return;
    try {
      await api.post(`/auctions/${id}/lottery`);
      toast.success(t("auction.toast.lottery_done"));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const isAdmin = user?.role === "admin";
  const isParticipant = user?.role === "participant";
  const highestBid = auction.bids?.[0];

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
      <div className="nv-border bg-[#0F4C3A] text-white rounded-md p-6 md:p-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Gavel className="w-6 h-6 text-[#D4AF37]" />
              <div className="text-xs tracking-[0.3em] uppercase text-[#D4AF37]">{t("auction.bidding")}</div>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-medium mt-3 tracking-tight">{t("auction.month")} {auction.month}</h1>
            <div className="text-[#CBE8DA] mt-3">
              {t("auction.status")}: <span className={`font-semibold ${auction.status === "open" ? "text-[#D4AF37] animate-pulse" : ""}`}>{auction.status.toUpperCase()}</span>
              {auction.opened_at && <> · {t("auction.opened")} {formatDateTime(auction.opened_at)}</>}
            </div>
          </div>
          {highestBid && (
            <div className="text-right">
              <div className="text-xs tracking-[0.25em] uppercase text-[#D4AF37]">{t("auction.highest_bid")}</div>
              <div className="font-mono-nv text-5xl md:text-6xl font-semibold mt-2">
                CHF {formatCHF(highestBid.amount)}
              </div>
              <div className="text-sm text-[#CBE8DA] mt-2">{t("auction.from")} {highestBid.user_name || highestBid.display_name}</div>
            </div>
          )}
        </div>
      </div>

      {/* Action area */}
      {auction.status === "open" && isParticipant && (
        <div className="nv-border bg-white rounded-md p-6 mt-6">
          <div className="font-heading text-xl text-[#0F4C3A] font-medium">{t("auction.place_bid_title")}</div>
          <p className="text-sm text-[#475569] mt-2">{t("auction.place_bid_subtitle")}</p>
          <form onSubmit={placeBid} className="mt-5 flex gap-3 flex-wrap">
            <div className="flex items-center border border-[#E2E8E4] rounded-md overflow-hidden">
              <button type="button" onClick={() => setAmount(Math.max(0, amount - 100))} className="px-4 py-3 text-lg font-semibold hover:bg-[#F8FAF9]">−</button>
              <input
                type="number"
                step="100"
                min="0"
                value={amount}
                onChange={(e) => setAmount(parseInt(e.target.value || "0"))}
                data-testid="bid-amount-input"
                className="w-40 px-4 py-3 text-center font-mono-nv text-xl border-0 focus:outline-none"
              />
              <button type="button" onClick={() => setAmount(amount + 100)} className="px-4 py-3 text-lg font-semibold hover:bg-[#F8FAF9]">+</button>
            </div>
            <button type="submit" disabled={loading} data-testid="place-bid-button" className="nv-btn-gold px-6 py-3 rounded-md font-medium">
              {loading ? "…" : t("auction.bid_btn", { amount: formatCHF(amount) })}
            </button>
          </form>
        </div>
      )}

      {isAdmin && auction.status === "open" && (
        <div className="flex gap-3 mt-6 flex-wrap">
          <button onClick={closeAuction} data-testid="close-auction-button" className="nv-btn-primary px-5 py-2.5 rounded-md font-medium inline-flex items-center gap-2">
            <Trophy className="w-4 h-4" /> {t("auction.close_btn")}
          </button>
          <button onClick={runLottery} data-testid="run-lottery-button" className="px-5 py-2.5 rounded-md font-medium inline-flex items-center gap-2 border border-[#D4AF37] text-[#0F4C3A] hover:bg-[#FEF9E7]">
            <Dices className="w-4 h-4" /> {t("auction.lottery_btn")}
          </button>
        </div>
      )}

      {auction.status === "closed" && (
        <div className="nv-border bg-[#FEF3C7] rounded-md p-6 mt-6 flex items-start gap-4">
          <Trophy className="w-6 h-6 text-[#92400E] mt-1" />
          <div>
            <div className="font-heading text-xl font-medium text-[#0F4C3A]">{t("auction.winner_title")}</div>
            <div className="text-[#475569] mt-1">
              {t("auction.winning_bid_label")} <span className="font-mono-nv font-semibold">CHF {formatCHF(auction.winning_bid || 0)}</span>
              {auction.by_lottery && <span className="ml-2 text-xs tracking-wider uppercase text-[#D4AF37]">{t("auction.by_lottery")}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Bids list */}
      <div className="nv-border bg-white rounded-md mt-6">
        <div className="p-5 border-b border-[#E2E8E4]">
          <div className="nv-label">{t("group.tab.auctions")}</div>
          <div className="font-heading text-lg font-medium text-[#0F4C3A]">{t("auction.bids_label", { count: auction.bids?.length || 0 })}</div>
        </div>
        <div className="divide-y divide-[#E2E8E4]">
          {auction.bids?.map((b, i) => (
            <div key={b.id} data-testid={`bid-row-${i}`} className={`px-5 py-3 flex items-center justify-between ${i === 0 ? "bg-[#F0FDF4]" : ""}`}>
              <div>
                <div className="font-medium text-[#0F4C3A]">
                  {b.is_self ? t("dashboard.you") : (b.user_name || b.display_name)}
                  {i === 0 && <span className="ml-2 text-xs tracking-wider uppercase text-[#D4AF37]">{t("auction.highest_bid")}</span>}
                </div>
                <div className="text-xs text-[#94A3B8]">{formatDateTime(b.created_at)}</div>
              </div>
              <div className="font-mono-nv font-semibold text-[#0F4C3A] text-lg">CHF {formatCHF(b.amount)}</div>
            </div>
          ))}
          {(!auction.bids || auction.bids.length === 0) && (
            <div className="p-8 text-center text-[#94A3B8]">{t("auction.no_bids")}</div>
          )}
        </div>
      </div>
    </div>
  );
}
